import React, { FC, memo, useRef, useEffect, useState } from 'react';
import { KeyboardArrowUp, Close } from '@mui/icons-material';
import { Fab, Grid, IconButton, Skeleton, Slide, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { SortButton } from './sortButton';
import { TicketGroup } from './ticketGroup';
import { TicketGroupsSkeleton } from '../../../components/skeletons/ticketGroupsSkeleton';
import { TicketGroupTEType, SelectedTicketGroupType } from '../book';
import { EventBookType } from '../../../core/models/events';
import { Order } from './order';
import { Footer } from './footer';
import { DistributionType } from '../../../store/distribution/types';

const useStyles = makeStyles()((theme) => ({
  ticketGroupsContainer: {
    height: '750px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    background: 'linear-gradient(180.62deg, rgba(28, 28, 28, 0) -91.79%, rgba(18, 17, 17, 0.41) 157.66%)',
    boxShadow: '4px -4px 16px rgb(0 0 0 / 63%), -4px 4px 16px rgba(0, 0, 0, 0.63)',
    borderRadius: '12px',
    padding: '32px',
    [theme.breakpoints.down(768)]: {
      height: 'min(750px, calc(100vh - 120px))',
      maxHeight: 'calc(100dvh - 80px)',
    },
    [theme.breakpoints.down(425)]: {
      padding: '18px 16px',
      height: 'min(750px, calc(100vh - 100px))',
      maxHeight: 'calc(100dvh - 60px)',
    },
  },
  container: {
    overflow: 'hidden',
    position: 'relative',
    height: '100%',
    minHeight: 0,
  },
  header: {
    display: 'flex',
    padding: '0 32px 29px 32px',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0,
    flexShrink: 0,
    [theme.breakpoints.down(600)]: {
      padding: '0 16px 20px 16px',
      gap: '8px',
    },
    [theme.breakpoints.down(425)]: {
      padding: '0 8px 16px 8px',
      gap: '6px',
    },
  },
  title: {
    fontSize: '0.75rem',
    cursor: 'default',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    [theme.breakpoints.down(425)]: {
      fontSize: '0.625rem',
    },
  },
  ticketGroups: {
    height: '100%',
    width: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollBehavior: 'smooth',
    padding: '0 12px',
    minHeight: 0,
    WebkitOverflowScrolling: 'touch',
    [theme.breakpoints.down(600)]: {
      padding: '0 10px',
    },
    [theme.breakpoints.down(425)]: {
      padding: '0 8px',
    },
  },
  downButton: {
    position: 'absolute',
    bottom: '16px',
    left: '16px',
    width: '37px',
    height: '37px',
    border: '0.5px solid #FFFFFF',
    color: theme.palette.primary.contrastText,

    [theme.breakpoints.down(425)]: {
      left: '50%',
      bottom: '-30px',
      transform: 'translate(-50%, -50%)',
    },
    ':hover': {
      backgroundColor: 'rgb(43,42,42)',
    },
  },
  skeletonSortButton: {
    borderRadius: '35px',
    width: '152px',
    height: '32px',
  },
  noTickets: {
    fontSize: '1.4rem',
    position: 'absolute',
    whiteSpace: 'nowrap',
    top: '45%',
    right: 0,
    left: 0,
    margin: 'auto',
    textAlign: 'center',
  },
  expandedPanel: {
    padding: '16px 0',
    margin: '0 -12px',
    //paddingTop: '48px',
    animation: 'slideDown 0.3s ease-out',
    position: 'relative',
    overflow: 'visible',
    '@keyframes slideDown': {
      from: {
        opacity: 0,
        transform: 'translateY(-10px)',
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
    [theme.breakpoints.down(600)]: {
      margin: '0 -10px',
      paddingTop: '44px',
    },
    [theme.breakpoints.down(425)]: {
      margin: '0 -8px',
      paddingTop: '40px',
    },
  },
  closeButton: {
    position: 'absolute',
    top: '48px',
    right: '16px',
    zIndex: 10,
    color: theme.palette.secondary.main,
    padding: '4px',
    border: '1px solid rgba(255, 191, 0, 0.4)',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 191, 0, 0.05)',
    '&:hover': {
      backgroundColor: 'rgba(255, 191, 0, 0.1)',
      borderColor: 'rgba(255, 191, 0, 0.6)',
    },
    [theme.breakpoints.down(425)]: {
      top: '40px',
      right: '8px',
    },
  },
}));

export const TicketGroups: FC<TicketGroupsType> = memo(({ 
  ticketGroups, 
  title, 
  eventSlug, 
  isLoading, 
  currency, 
  onTicketGroupClick,
  selectedTicketGroup,
  event,
  handleTicketsCount,
  totalPrice,
  distribution,
  selectedTicketGroups,
  setSelectedTicketGroups,
  isLoadingCheckout,
  paymentHandleClick,
}) => {
  const { classes } = useStyles();
  const ticketGroupsRef = useRef(null);
  const expandedPanelRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [shouldGlow, setShouldGlow] = useState(false);
  const prevSelectedTicketGroupRef = useRef<TicketGroupTEType | null>(null);

  const scrollTop = () => {
    const ticketGroupsElement = ticketGroupsRef.current;
    ticketGroupsElement.scrollTo(0, 0);
  };

  const scrollToFooter = () => {
    if (footerRef.current) {
      // Небольшая задержка для завершения обновления UI после выбора количества
      setTimeout(() => {
        // Сначала пытаемся прокрутить внутри контейнера
        if (ticketGroupsRef.current && footerRef.current) {
          const containerRect = ticketGroupsRef.current.getBoundingClientRect();
          const footerRect = footerRef.current.getBoundingClientRect();
          
          // Если footer внутри контейнера, прокручиваем контейнер
          if (footerRect.top >= containerRect.top && footerRect.bottom <= containerRect.bottom) {
            const scrollTop = ticketGroupsRef.current.scrollTop;
            const footerOffsetTop = footerRect.top - containerRect.top + scrollTop;
            ticketGroupsRef.current.scrollTo({
              top: footerOffsetTop - 50, // Небольшой отступ сверху
              behavior: 'smooth',
            });
          } else {
            // Иначе прокручиваем window
            footerRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        } else {
          // Если контейнер не найден, прокручиваем window
          footerRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 150);
    }
  };

  useEffect(() => {
    // Отслеживаем момент, когда панель только что открылась
    const prevId = prevSelectedTicketGroupRef.current?.id;
    const currentId = selectedTicketGroup?.id;
    
    if (selectedTicketGroup && prevId !== currentId) {
      // Панель только что открылась (переход от null к объекту или изменение ID)
      setShouldGlow(true);
    } else if (!selectedTicketGroup) {
      // Панель закрылась
      setShouldGlow(false);
    }
    
    prevSelectedTicketGroupRef.current = selectedTicketGroup;
  }, [selectedTicketGroup]);

  useEffect(() => {
    if (selectedTicketGroup && expandedPanelRef.current && ticketGroupsRef.current) {
      // Небольшая задержка для завершения анимации раскрытия
      const timer = setTimeout(() => {
        const panelElement = expandedPanelRef.current;
        const containerElement = ticketGroupsRef.current;
        
        if (panelElement && containerElement) {
          // Вычисляем позицию панели относительно контейнера
          const containerRect = containerElement.getBoundingClientRect();
          const panelRect = panelElement.getBoundingClientRect();
          
          // Прокручиваем контейнер так, чтобы панель была видна
          const scrollTop = containerElement.scrollTop;
          const panelOffsetTop = panelRect.top - containerRect.top + scrollTop;
          
          // Прокручиваем с отступом сверху, чтобы строка листинга и крестик были видны полностью
          // Учитываем высоту строки листинга (примерно 50-60px) и отступ для крестика
          containerElement.scrollTo({
            top: panelOffsetTop - 40,
            behavior: 'smooth',
          });
        }
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [selectedTicketGroup]);

  // @ts-ignore
  return (
    <Grid className={classes.ticketGroupsContainer}>
      <header className={classes.header}>
        <Typography className={classes.title} component={'div'} alignSelf={'center'}>
          {isLoading && <Skeleton animation="wave" variant="rectangular" width={'70px'} height={'18px'} />}
          {!isLoading && title}
        </Typography>
        {isLoading && <Skeleton animation="wave" variant="rectangular" className={classes.skeletonSortButton} />}
        {!isLoading && <SortButton />}
      </header>
      <Grid container className={classes.container}>
        <Grid item className={classes.ticketGroups} ref={ticketGroupsRef}>
          {isLoading && <TicketGroupsSkeleton />}
          {!isLoading && ticketGroups.map((ticketGroup, index) => (
            <React.Fragment key={ticketGroup.id}>
              <TicketGroup 
                ticketGroup={ticketGroup} 
                eventSlug={eventSlug} 
                currency={currency} 
                onTicketGroupClick={onTicketGroupClick}
                isSelected={selectedTicketGroup?.id === ticketGroup.id}
              />
              {selectedTicketGroup?.id === ticketGroup.id && (
                <div ref={expandedPanelRef} className={classes.expandedPanel}>
                  <IconButton
                    className={classes.closeButton}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (onTicketGroupClick) {
                        onTicketGroupClick(ticketGroup);
                      }
                    }}
                    size="small"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                  <Order
                    event={event}
                    ticketGroup={selectedTicketGroup}
                    handleTicketsCount={handleTicketsCount}
                    totalPrice={totalPrice}
                    distribution={distribution}
                    setSelectedTicketGroups={setSelectedTicketGroups}
                    shouldGlow={shouldGlow}
                    scrollToFooter={scrollToFooter}
                  />
                  <div ref={footerRef}>
                    <Footer
                      event={event}
                      selectedTicketGroups={selectedTicketGroups}
                      isLoading={isLoadingCheckout}
                      paymentHandleClick={paymentHandleClick}
                    />
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
          {!isLoading && !ticketGroups?.length && (
            <Slide direction="down" in={!ticketGroups?.length} container={ticketGroupsRef.current}>
              <Typography className={classes.noTickets} component={'div'}>
                There aren't any tickets
              </Typography>
            </Slide>
          )}
        </Grid>
      </Grid>
      <Fab className={classes.downButton} onClick={scrollTop}>
        <KeyboardArrowUp />
      </Fab>
    </Grid>
  );
});

type TicketGroupsType = {
  eventSlug: string;
  ticketGroups: TicketGroupTEType[];
  title: string;
  isLoading: boolean;
  currency: string;
  onTicketGroupClick?: (ticketGroup: TicketGroupTEType) => void;
  selectedTicketGroup?: TicketGroupTEType | null;
  event?: EventBookType | null;
  handleTicketsCount?: (count: number, ticketGroup: TicketGroupTEType) => void;
  totalPrice?: string;
  distribution?: DistributionType;
  selectedTicketGroups?: SelectedTicketGroupType[];
  setSelectedTicketGroups?: React.Dispatch<React.SetStateAction<SelectedTicketGroupType[]>>;
  isLoadingCheckout?: boolean;
  paymentHandleClick?: () => Promise<void>;
};
